import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ActivatedRoute, Router } from '@angular/router';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { ToastrService } from 'ngx-toastr';
import { StaticApiService } from '../../features/static-api/staticApi.service';
import { QuillModule } from 'ngx-quill';
import { EditorModule } from '@tinymce/tinymce-angular';
import { environment } from '../../../environment/environment';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { MatDialog } from '@angular/material/dialog';
import {
  noOnlySpacesValidator,
  noOnlySpecialCharsValidator,
} from '../../core/helpers/validator';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';

@Component({
  selector: 'app-new-static-pages',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    CommonModule,
    QuillModule,
    EditorModule,
    ContentLoaderComponent,
  ],
  templateUrl: './static-page-new.component.html',
  styleUrl: './static-page-new.component.scss',
})
export class StaticPageNew {
  readonly dialog = inject(MatDialog);
  public languages: any[] = [];
  public form: FormGroup;
  public staticTranslations: {
    id?: number;
    langId: number;
    content: string;
  }[] = [];
  pageId: any;
  keyValue: any;
  pageKeys: any;
  addWarning: boolean = true;
  isSubmitting = false;
  pageData: any;
  currLangId: number = 1;
  key: string = '';
  isEditing: boolean = false;
  contentLoading: boolean = false;
  tinyInit = {
    plugins: [
      'anchor',
      'autolink',
      'charmap',
      'codesample',
      'emoticons',
      'image',
      'link',
      'lists',
      'media',
      'searchreplace',
      'table',
      'visualblocks',
      'wordcount',
    ],
    toolbar:
      'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough forecolor backcolor | ' +
      'link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat',

    font_family_formats:
      'Fredoka=Fredoka, sans-serif; Arial=arial,helvetica,sans-serif; Courier New=courier new,courier,monospace; Times New Roman=times new roman,times;',
    content_style: `
    @import url('https://fonts.googleapis.com/css2?family=Fredoka&display=swap');
    body { font-family: Fredoka, sans-serif; }
    `,
  };
  tinyMceApiKey: string = environment.tinyMceApiKey;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private tagsService: TagsLanguageService,
    private staticService: StaticApiService,
    private toastr: ToastrService,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      key: [
        '',
        [
          Validators.required,
          Validators.maxLength(40),
          noOnlySpacesValidator(),
          noOnlySpecialCharsValidator(),
        ],
      ],
      content: [''],
      langId: [1, Validators.required],
    });

    enum StaticPageKey {
      contact_us = 'Contact Us',
      terms_and_conditions = 'Terms and Conditions',
      privacy_policy = 'Privacy Policy',
      about_us = 'About Us',
    }

    const PageKeyOptions = Object.entries(StaticPageKey).map(
      ([key, value]) => ({
        key,
        value,
      })
    );
    this.pageKeys = PageKeyOptions;
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.pageId = params.get('id');
      if (this.pageId) {
        this.contentLoading = true;
        this.form.get('langId')?.patchValue(null);
        this.getStaticPageById(this.pageId);
        this.form.get('key')?.disable();
      }
    });
    this.getLanguages();
  }

  getLanguages(): void {
    this.tagsService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
        }
        if (this.staticTranslations.length === this.languages.length) {
          this.form.get('langId')?.disable();
          this.addWarning = false;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getLanguageName(langId: number): string {
    const lang = this.languages.find((l) => l.id === langId);
    return lang ? lang.name : `Lang ID: ${langId}`;
  }
  getStaticPageById(id: any) {
    this.staticService.getStaticContentById(id).subscribe({
      next: (res) => {
        this.pageData = res?.data;
        if (this.pageData) {
          const htmlString = this.pageData.key;
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlString;
          const plainText = tempDiv.textContent || tempDiv.innerText || '';
          this.form.patchValue({
            key: plainText,
          });
          this.keyValue = plainText;
          this.staticTranslations = [];
          this.pageData.translations.forEach((translation: any) => {
            this.staticTranslations.push({
              id: translation.id,
              langId: translation.langId,
              content: translation.content,
            });
          });
          if (this.staticTranslations.length === this.languages.length) {
            this.form.get('langId')?.disable();
            this.addWarning = false;
          }
        }
        this.contentLoading = false;
      },
      error: (err) => {
        this.contentLoading = false;
      },
    });
  }

  addStaticTable(lang?: number): void {
    this.form.get('key')?.enable();
    this.form.get('langId')?.enable();
    const { content, key } = this.form.value;
    const langId = lang ? lang : this.form.value.langId;
    this.key = key.trim();
    if (!key) {
      this.toastr.warning('Please enter valid key.');
    }
    if (this.form.invalid) {
      this.toastr.warning('Please fill in all fields to continue.');
      if (this.pageId) {
        this.form.get('key')?.disable();
      }
      return;
    }
    if (content === '' || content === null) {
      this.toastr.warning('Please enter valid content.');
      if (this.pageId) {
        this.form.get('key')?.disable();
      }
      return;
    }
    this.staticTranslations.push({ content, langId });
    this.keyValue = key;
    this.form.patchValue({
      content: null,
      langId: null,
    });
    this.form.get('content')?.markAsUntouched();
    this.form.get('langId')?.markAsUntouched();
    if (this.staticTranslations.length === this.languages.length) {
      this.addWarning = false;
      this.form.get('langId')?.disable();
    }
    this.form.get('key')?.disable();
    this.isEditing = false;
  }

  get sortedTranslations() {
    return this.staticTranslations
      ?.slice()
      .sort((a, b) => (a.langId === 1 ? -1 : b.langId === 1 ? 1 : 0));
  }

  onLangSelect(langId: any) {
    this.isEditing = true;
    this.currLangId = langId;
    const langCode = this.languages.find((l) => l.id === langId).code;
    if (this.pageId) {
      this.staticService
        .getStaticContentByIdLangCode(this.pageId, langCode)
        .subscribe({
          next: (res) => {
            if (res?.data) {
              this.form.patchValue({
                content: res?.data.translations[0].content,
              });
            }
          },
          error: (err) => {
            console.log(err);
          },
        });
    }
  }

  isLangUsed(langId: number): boolean {
    return this.staticTranslations?.some((t) => t.langId === langId);
  }

  removeStaticTranslation(index: number, id: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        if (!id) {
          this.staticTranslations.splice(index, 1);
        } else {
          this.staticService.deleteStaticContent(id).subscribe({
            next: (res) => {
              this.staticTranslations.splice(index, 1);
              this.form.get('langId')?.enable();
            },
            error: (err) => {
              console.log(err);
            },
          });
        }
      }
    });
  }

  editStaticTranslation(index: number): void {
    this.form.get('langId')?.disable();
    this.form.get('key')?.disable();
    if (this.form.value.content) {
      this.addStaticTable();
    }
    this.isEditing = true;
    const selected = this.staticTranslations[index];
    this.staticTranslations.splice(index, 1);
    this.form.patchValue({
      content: selected.content,
      langId: selected.langId,
    });
    this.form.get('langId')?.disable();
    this.form.get('content')?.markAsTouched();
    this.form.get('langId')?.markAsTouched();
    this.scrollUp(`translation`);
  }

  scrollUp(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  onCancel() {
    this.router.navigate(['/info-pages']);
  }

  saveSTaticPage(): void {
    if (this.form.value.content) {
      this.addStaticTable();
    }
    this.form.get('key')?.enable();
    this.form.get('langId')?.enable();

    if (
      !this.staticTranslations.some((translation) => translation.langId === 1)
    ) {
      this.toastr.error(
        'Static Page in English is mandatory, Please add it to continue'
      );
      if (this.staticTranslations.length !== 0) {
        this.form.get('key')?.disable();
        this.form.get('langId')?.disable();
      }
      return;
    }
    if (!this.form.value.key) {
      this.toastr.warning('Please enter a key value.');
      return;
    }

    const payload = {
      key: this.form.get('key')?.value || this.keyValue,
      translations: this.staticTranslations,
    };

    this.isSubmitting = true;
    if (this.pageId) {
      this.staticService.updateStaticPage(this.pageId, payload).subscribe({
        next: (res) => {
          this.toastr.success('Static content updated successfully.');
          this.isSubmitting = false;
          this.form.reset();
          this.router.navigate(['/info-pages']);
        },
        error: (err) => {
          this.toastr.error('Failed to update static content.');
          this.isSubmitting = false;
        },
      });
    } else {
      this.staticService.createStaticPage(payload).subscribe({
        next: (res) => {
          this.toastr.success('Static content created successfully.');
          this.form.reset();
          this.router.navigate(['/info-pages']);
        },
        error: (err) => {
          this.form.reset();
        },
      });
    }
    this.form.get('key')?.disable();
    this.form.get('langId')?.disable();
  }
}
